import { SpaPropertyEnum } from "../../constants/NavbarList";
import {
  normalizeSpaServiceLinkedSelections,
  resolveSpaServiceGroupCode,
  SPA_SERVICES_FIELD_LINKED_ITEM,
  SPA_SERVICES_FIELD_PRICE_TYPE,
  SPA_SERVICES_FIELD_SERVICE_CODE,
  SPA_SERVICES_FIELD_SERVICE_EQUIPMENT,
  SPA_SERVICES_FIELD_SERVICE_GROUP,
  SPA_SERVICES_FIELD_SERVICE_GROUP_CODE,
  SPA_SERVICES_FIELD_SERVICE_LOCATIONS,
  SPA_SERVICES_FIELD_SERVICE_PRICE,
} from "../../constants/SpaServicesFields";
import { isEmptyValue, normalizeSheetRows } from "./payloadRows";
import { resolveRoomDefinitionListOrder } from "./roomDefinitionRows";
import { isSampleTestRow } from "./sampleDataFilter";

/** `"CODE - Description"` → `CODE`; plain values pass through. */
export const extractLinkedOptionCode = (value: unknown): string => {
  if (value == null || String(value).trim() === "") {
    return "";
  }
  const str = String(value).trim();
  const separator = str.indexOf(" - ");
  return (separator >= 0 ? str.slice(0, separator) : str).trim();
};

const toStringArray = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value
      .filter((item) => item != null && String(item).trim() !== "")
      .map((item) => String(item).trim());
  }
  if (value != null && String(value).trim() !== "") {
    return [String(value).trim()];
  }
  return [];
};

const readServiceCode = (service: Record<string, unknown>): string =>
  extractLinkedOptionCode(service[SPA_SERVICES_FIELD_SERVICE_CODE]);

const readServiceGroupCode = (service: Record<string, unknown>): string =>
  extractLinkedOptionCode(
    service[SPA_SERVICES_FIELD_SERVICE_GROUP_CODE] ??
      service[SPA_SERVICES_FIELD_SERVICE_GROUP],
  );

const serializeLinkedCodes = (value: unknown): string =>
  toStringArray(value).map(extractLinkedOptionCode).filter(Boolean).join(",");

const isExportableServiceRow = (service: Record<string, unknown>): boolean => {
  if (isSampleTestRow(service)) {
    return false;
  }
  return (
    Boolean(readServiceCode(service)) && Boolean(readServiceGroupCode(service))
  );
};

const enrichServiceExportRow = (
  service: Record<string, unknown>,
  index: number,
): Record<string, unknown> => ({
  ...service,
  [SPA_SERVICES_FIELD_SERVICE_LOCATIONS]: serializeLinkedCodes(
    service[SPA_SERVICES_FIELD_SERVICE_LOCATIONS],
  ),
  [SPA_SERVICES_FIELD_LINKED_ITEM]: extractLinkedOptionCode(
    service[SPA_SERVICES_FIELD_LINKED_ITEM],
  ),
  "List Order": resolveRoomDefinitionListOrder(service["List Order"], index),
});

/** Backfills Service Group Code on service rows before Excel mapping. */
export const enrichSpaServicesExportPayload = (
  payload: Record<string, unknown>,
): void => {
  const services = payload[SpaPropertyEnum.services];
  const groups = payload[SpaPropertyEnum.serviceGroups];
  const priceTypes = payload[SpaPropertyEnum.priceTypes];
  const serviceEquipment = payload[SpaPropertyEnum.serviceEquipment];
  if (!Array.isArray(services)) {
    return;
  }

  for (const service of services) {
    if (!service || typeof service !== "object") {
      continue;
    }
    const record = service as Record<string, unknown>;
    if (isSampleTestRow(record)) {
      continue;
    }
    if (isEmptyValue(record[SPA_SERVICES_FIELD_SERVICE_GROUP_CODE])) {
      record[SPA_SERVICES_FIELD_SERVICE_GROUP_CODE] =
        resolveSpaServiceGroupCode(
          record[SPA_SERVICES_FIELD_SERVICE_GROUP],
          groups,
        );
    }
    normalizeSpaServiceLinkedSelections(record, priceTypes, serviceEquipment);
  }
};

/** Skips demo rows and services missing code or service group code. */
export const mapServiceDetailRows = (
  sheetData: unknown,
): Record<string, unknown>[] => {
  const rows: Record<string, unknown>[] = [];

  for (const service of normalizeSheetRows(sheetData)) {
    if (!isExportableServiceRow(service)) {
      continue;
    }
    rows.push(enrichServiceExportRow(service, rows.length));
  }

  return rows;
};

/** One ServicePriceType row per selected price type on each service. */
export const mapServicePriceTypeRows = (
  sheetData: unknown,
): Record<string, unknown>[] => {
  const rows: Record<string, unknown>[] = [];

  for (const service of normalizeSheetRows(sheetData)) {
    if (isSampleTestRow(service)) {
      continue;
    }

    const serviceCode = readServiceCode(service);
    if (!serviceCode) {
      continue;
    }

    const priceTypes = toStringArray(service[SPA_SERVICES_FIELD_PRICE_TYPE]);
    const price = service[SPA_SERVICES_FIELD_SERVICE_PRICE] ?? "0.00";

    for (const priceType of priceTypes) {
      const priceTypeCode = extractLinkedOptionCode(priceType);
      if (!priceTypeCode) {
        continue;
      }
      rows.push({
        [SPA_SERVICES_FIELD_SERVICE_CODE]: serviceCode,
        "Price Type Code": priceTypeCode,
        Price: price,
      });
    }
  }

  return rows;
};

/** One ServiceEquipment row per selected equipment item on each service. */
export const mapServiceEquipmentRows = (
  sheetData: unknown,
): Record<string, unknown>[] => {
  const rows: Record<string, unknown>[] = [];

  for (const service of normalizeSheetRows(sheetData)) {
    if (isSampleTestRow(service)) {
      continue;
    }

    const serviceCode = readServiceCode(service);
    if (!serviceCode) {
      continue;
    }

    const equipmentItems = toStringArray(
      service[SPA_SERVICES_FIELD_SERVICE_EQUIPMENT],
    );

    for (const equipment of equipmentItems) {
      const equipmentCode = extractLinkedOptionCode(equipment);
      if (!equipmentCode) {
        continue;
      }
      rows.push({
        [SPA_SERVICES_FIELD_SERVICE_CODE]: serviceCode,
        "Equipment Code": equipmentCode,
        "Require Quantity": 0,
      });
    }
  }

  return rows;
};

/** One ServiceAvailableDays row per service with the same filter as ServiceDetail. */
export const mapServiceAvailableDaysRows = (
  sheetData: unknown,
): Record<string, unknown>[] => {
  const rows: Record<string, unknown>[] = [];

  for (const service of normalizeSheetRows(sheetData)) {
    if (!isExportableServiceRow(service)) {
      continue;
    }
    rows.push({ ...service });
  }

  return rows;
};

const collectMasterCodes = (rows: unknown, field: string): Set<string> => {
  const codes = new Set<string>();
  for (const row of normalizeSheetRows(rows)) {
    if (isSampleTestRow(row)) {
      continue;
    }
    const code = extractLinkedOptionCode(row[field]);
    if (code) {
      codes.add(code);
    }
  }
  return codes;
};

/** Validates service rows reference master codes defined in onboarding payload. */
export const validateSpaServiceImportReferences = (
  payload: Record<string, unknown>,
): string[] => {
  enrichSpaServicesExportPayload(payload);

  const groupCodes = collectMasterCodes(
    payload[SpaPropertyEnum.serviceGroups],
    "Service Group Code",
  );
  const priceTypeCodes = collectMasterCodes(
    payload[SpaPropertyEnum.priceTypes],
    "Price Type Code",
  );

  const errors: string[] = [];

  for (const service of normalizeSheetRows(payload[SpaPropertyEnum.services])) {
    if (isSampleTestRow(service)) {
      continue;
    }

    const serviceCode = readServiceCode(service);
    if (!serviceCode) {
      continue;
    }

    const groupCode = readServiceGroupCode(service);
    if (groupCode && !groupCodes.has(groupCode)) {
      errors.push(
        `Service "${serviceCode}": Service Group Code "${groupCode}" is missing from Service Groups. Import ServiceGroups before ServiceDetail.`,
      );
    }

    for (const priceType of toStringArray(
      service[SPA_SERVICES_FIELD_PRICE_TYPE],
    )) {
      const priceTypeCode = extractLinkedOptionCode(priceType);
      if (priceTypeCode && !priceTypeCodes.has(priceTypeCode)) {
        errors.push(
          `Service "${serviceCode}": Price Type Code "${priceTypeCode}" is missing from Price Types. Import PriceTypes before ServiceDetail.`,
        );
      }
    }
  }

  return errors;
};
